import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Search, User, Phone, MapPin, Wrench, CheckCircle, X, Loader2, Copy, FileText, Edit2, ExternalLink, Clock, Plus } from 'lucide-react';
import { ServiceAdvisorService } from '../../services/serviceAdvisorService';
import { MechanicLogsService } from '../../services/mechanicLogsService';

const AssignMechanic = () => {
  const { id } = useParams(); // complaintId
  const navigate = useNavigate();
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');

  // Get complaint data from location state or fetch
  const complaintFromState = location.state?.complaint;
  const [activeTab, setActiveTab] = useState('available'); // 'available' or 'search'
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [selectedMechanic, setSelectedMechanic] = useState(null);
  const pincodeInitialized = useRef(false);
  const [showPincodeModal, setShowPincodeModal] = useState(false);
  const [pincodeInput, setPincodeInput] = useState('');
  const [isUpdatingPincode, setIsUpdatingPincode] = useState(false);
  const [showAddMechanicModal, setShowAddMechanicModal] = useState(false);
  const [isAddingMechanic, setIsAddingMechanic] = useState(false);
  const [newMechanic, setNewMechanic] = useState({
    mechanic_name: '',
    number: '',
    alternate_number1: '',
    alternate_number2: '',
    service_tag: '',
    service_tag2: '',
    service_tag3: '',
    timing: '',
    service_areas: [{ area: '', pincode: '', state: '' }],
  });
  const queryClient = useQueryClient();

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch complaint details
  const { data: complaintData, refetch: refetchComplaint } = useQuery({
    queryKey: ['serviceAdvisorComplaintDetail', id],
    queryFn: async () => {
      const result = await ServiceAdvisorService.getComplaintById(id);
      return result.data;
    },
    enabled: !!id,
  });

  // Merge complaint data - prioritize fetched data over state
  const complaint = useMemo(() => {
    return complaintData || complaintFromState || {};
  }, [complaintData, complaintFromState]);

  const resolvedPincode = useMemo(() => {
    const texts = [
      complaint?.pincode,
      complaint?.pickup_address,
      complaint?.address,
      complaint?.drop_location,
      complaint?.service_location,
      complaint?.location_link,
    ];
    for (const t of texts) {
      const m = String(t || '').match(/\b(\d{6})\b/);
      if (m?.[1]) return m[1];
    }
    return '';
  }, [complaint]);

  // Prefill Add Mechanic modal with complaint pincode
  useEffect(() => {
    if (!showAddMechanicModal) return;
    setNewMechanic((prev) => {
      const arr = Array.isArray(prev.service_areas) && prev.service_areas.length ? [...prev.service_areas] : [{ area: '', pincode: '', state: '' }];
      if (!arr[0]) arr[0] = { area: '', pincode: '', state: '' };
      arr[0] = { ...arr[0], pincode: arr[0].pincode || complaint?.pincode || '' };
      return { ...prev, service_areas: arr };
    });
  }, [showAddMechanicModal, complaint?.pincode]);

  // Clear search query if it matches pincode (to avoid filtering out mechanics)
  useEffect(() => {
    if (complaint?.pincode && searchQuery === complaint.pincode) {
      console.log('🧹 Clearing search query that matches pincode');
      setSearchQuery('');
    }
  }, [complaint?.pincode, searchQuery]);

  // Fetch available mechanics
  const { data: mechanicsData, isLoading: isLoadingMechanics, refetch: refetchMechanics } = useQuery({
    queryKey: ['serviceAdvisorAvailableMechanics', resolvedPincode, complaint?.service_type],
    queryFn: () => {
      return ServiceAdvisorService.getAvailableMechanics(
        complaint?.service_type || null,
        null,
        resolvedPincode || null
      );
    },
    enabled: !!id && activeTab === 'available',
  });

  // Fetch search results for unregistered mechanics
  const { data: searchResultsData, isLoading: isLoadingSearch } = useQuery({
    queryKey: ['serviceAdvisorSearchMechanics', debouncedSearchQuery, complaint?.service_type],
    queryFn: () => ServiceAdvisorService.searchMechanics(
      debouncedSearchQuery,
      complaint?.service_type || null
    ),
    enabled: !!id && activeTab === 'search' && debouncedSearchQuery.trim().length > 0,
  });

  // Memoize mechanics array to prevent unnecessary re-renders
  const mechanics = useMemo(() => {
    const mechs = mechanicsData?.data || [];
    console.log('🔧 Mechanics data:', mechs.length, 'mechanics found');
    if (mechs.length > 0) {
      console.log('🔧 First mechanic:', mechs[0]);
    }
    return mechs;
  }, [mechanicsData?.data]);

  const searchResults = useMemo(() => {
    return searchResultsData?.data || [];
  }, [searchResultsData?.data]);

  // Filter mechanics by search query - use useMemo instead of useEffect to prevent infinite loop
  const filteredMechanics = useMemo(() => {
    console.log('🔍 Filtering mechanics. Total:', mechanics.length, 'Search query:', searchQuery);
    
    if (!searchQuery.trim()) {
      console.log('🔍 No search query, returning all mechanics:', mechanics.length);
      return mechanics;
    }

    const query = searchQuery.toLowerCase();
    const filtered = mechanics.filter((mechanic) => {
      // Handle different field name variations (c_name, name, mechanic_name)
      const name = (mechanic.c_name || mechanic.name || mechanic.mechanic_name || '').toLowerCase();
      const phone = (mechanic.contact_number || mechanic.number || '').toLowerCase();
      const serviceTag = (mechanic.service_tag || '').toLowerCase();
      const pincode = (mechanic.pincode || '').toString();

      const matches = (
        name.includes(query) ||
        phone.includes(query) ||
        serviceTag.includes(query) ||
        pincode.includes(query)
      );
      
      if (matches) {
        console.log('🔍 Match found:', name, phone);
      }
      
      return matches;
    });
    
    console.log('🔍 Filtered mechanics:', filtered.length);
    return filtered;
  }, [searchQuery, mechanics]);

  const handleMechanicSelect = (mechanic) => {
    setSelectedMechanic(mechanic);
  };

  const handleProceed = () => {
    if (!selectedMechanic) {
      alert('Please select a mechanic');
      return;
    }

    // Navigate to AssignmentDetails with selected mechanic
    navigate(`/service_advisor/assignment-details/${id}`, {
      state: {
        selectedMechanic: selectedMechanic,
        complaint: complaint,
      },
    });
  };

  const handleCall = async (phoneNumber, mechanic) => {
    if (!phoneNumber) return;
    
    try {
      const logsService = MechanicLogsService.getInstance();
      
      // Log the call action
      if (mechanic) {
        await logsService.addLog({
          mechanicId: mechanic.id,
          mechanicName: mechanic.c_name || mechanic.name || mechanic.mechanic_name || 'Unknown',
          action: 'call',
          details: `Called mechanic: ${phoneNumber}`,
          userId: user?.id,
          userName: user?.name || user?.email || 'Unknown',
          complaintId: id,
          phoneNumber: phoneNumber
        });
      }
      
      window.location.href = `tel:${phoneNumber}`;
    } catch (error) {
      console.error('Error logging call:', error);
      window.location.href = `tel:${phoneNumber}`;
    }
  };

  const handleCopyMechanicInfo = async (mechanic) => {
    try {
      const logsService = MechanicLogsService.getInstance();
      const info = [
        `Name: ${mechanic.c_name || mechanic.name || mechanic.mechanic_name || 'N/A'}`,
        `Phone: ${mechanic.contact_number || mechanic.number || 'N/A'}`,
        `Pincode: ${mechanic.pincode || 'N/A'}`,
        `Service Tag: ${mechanic.service_tag || 'N/A'}`,
        `Timing: ${mechanic.timing || mechanic.timings || 'N/A'}`,
        `Service Areas: ${formatServiceAreas(mechanic.service_areas)}`
      ].join('\n');
      await navigator.clipboard.writeText(info);
      
      // Log the copy action
        await logsService.addLog({
          mechanicId: mechanic.id,
          mechanicName: mechanic.c_name || mechanic.name || mechanic.mechanic_name || 'Unknown',
          action: 'copy_info',
          details: `Copied mechanic information: ${info}`,
          userId: user?.id,
          userName: user?.name || user?.email || 'Unknown',
          complaintId: id,
          phoneNumber: mechanic.contact_number || mechanic.number
        });
      
      alert('Mechanic info copied to clipboard!');
    } catch (error) {
      console.error('Error copying mechanic info:', error);
      alert('Failed to copy mechanic information');
    }
  };

  const handleViewLogs = async (mechanic) => {
    try {
      const logsService = MechanicLogsService.getInstance();
      
      // Log the view details action
        await logsService.addLog({
          mechanicId: mechanic.id,
          mechanicName: mechanic.c_name || mechanic.name || mechanic.mechanic_name || 'Unknown',
          action: 'view_details',
          details: `Viewed mechanic logs for ${mechanic.c_name || mechanic.name || mechanic.mechanic_name || 'Unknown'}`,
          userId: user?.id,
          userName: user?.name || user?.email || 'Unknown',
          complaintId: id,
          phoneNumber: mechanic.contact_number || mechanic.number
        });
      
      navigate(`/service_advisor/mechanics/${mechanic.id}/logs`, {
        state: { mechanic: mechanic }
      });
    } catch (error) {
      console.error('Error logging view:', error);
      navigate(`/service_advisor/mechanics/${mechanic.id}/logs`, {
        state: { mechanic: mechanic }
      });
    }
  };

  const formatServiceAreas = (serviceAreas) => {
    if (!serviceAreas) return 'N/A';
    if (Array.isArray(serviceAreas)) {
      return serviceAreas.map((area) => 
        typeof area === 'object' ? (area.area || area.pincode || JSON.stringify(area)) : area
      ).join(', ');
    }
    if (typeof serviceAreas === 'object') {
      return serviceAreas.area || serviceAreas.pincode || JSON.stringify(serviceAreas);
    }
    return String(serviceAreas);
  };

  const handleUpdatePincode = async () => {
    // Validation - pincode should be 6 digits
    const pincodeRegex = /^\d{6}$/;
    if (!pincodeInput.trim()) {
      alert('Please enter a pincode');
      return;
    }
    if (!pincodeRegex.test(pincodeInput.trim())) {
      alert('Pincode must be exactly 6 digits');
      return;
    }

    setIsUpdatingPincode(true);
    try {
      const result = await ServiceAdvisorService.updateComplaintPincode(
        id,
        pincodeInput.trim(),
        user?.id
      );
      
      if (result.success) {
        // Update local complaint data immediately
        setShowPincodeModal(false);
        const newPincode = pincodeInput.trim();
        setPincodeInput('');
        
        // Clear search query so mechanics are not filtered
        setSearchQuery('');
        
        // Refetch complaint data immediately to show updated pincode
        const updatedComplaintResult = await refetchComplaint();
        console.log('✅ Complaint refetched after pincode update:', updatedComplaintResult?.data?.pincode);
        
        // Also invalidate queries to ensure all related data is refreshed
        queryClient.invalidateQueries(['serviceAdvisorComplaintDetail', id]);
        queryClient.invalidateQueries(['serviceAdvisorAvailableMechanics']);
        
        // Wait a bit for the complaint data to update, then refetch mechanics
        setTimeout(async () => {
          console.log('🔄 Refetching mechanics after pincode update with pincode:', newPincode);
          const mechanicsResult = await refetchMechanics();
          console.log('✅ Mechanics refetched:', mechanicsResult?.data?.data?.length || 0, 'mechanics');
        }, 1000);
        
        alert('Success: ' + (result.message || 'Pincode updated successfully!'));
      } else {
        alert('Failed to update pincode: ' + (result.error || result.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error updating pincode:', error);
      alert('Error updating pincode: ' + error.message);
    } finally {
      setIsUpdatingPincode(false);
    }
  };

  const handleLocationClick = () => {
    if (!complaint.location_link) {
      alert('Location link not available');
      return;
    }

    // Open Google Maps with the location link (Plus Code or coordinates)
    const locationUrl = `https://www.google.com/maps/search/${encodeURIComponent(complaint.location_link)}`;
    window.open(locationUrl, '_blank');
  };

  const generateMechanicCode = () => {
    const tail = String(Date.now()).slice(-6);
    return `M${tail}`;
  };

  const handleSaveNewMechanic = async () => {
    const name = (newMechanic.mechanic_name || '').trim();
    const numDigits = (newMechanic.number || '').replace(/\D/g, '');
    if (!name) return alert('Mechanic name is required');
    if (numDigits.length < 10) return alert('Mechanic number must be at least 10 digits');

    setIsAddingMechanic(true);
    try {
      const res = await ServiceAdvisorService.addMechanicSimple({
        code: generateMechanicCode(),
        mechanic_name: name,
        number: numDigits,
        alternate_number1: (newMechanic.alternate_number1 || '').replace(/\D/g, '') || null,
        alternate_number2: (newMechanic.alternate_number2 || '').replace(/\D/g, '') || null,
        service_tag: (newMechanic.service_tag || '').trim() || null,
        service_tag2: (newMechanic.service_tag2 || '').trim() || null,
        service_tag3: (newMechanic.service_tag3 || '').trim() || null,
        timing: (newMechanic.timing || '').trim() || null,
        service_areas: Array.isArray(newMechanic.service_areas) ? newMechanic.service_areas : [],
      });
      if (!res.success) throw new Error(res.error || 'Failed to add mechanic');

      setShowAddMechanicModal(false);
      setNewMechanic({
        mechanic_name: '',
        number: '',
        alternate_number1: '',
        alternate_number2: '',
        service_tag: '',
        service_tag2: '',
        service_tag3: '',
        timing: '',
        service_areas: [{ area: '', pincode: complaint?.pincode || '', state: '' }],
      });
      // Refresh mechanics list and move to Available tab
      setActiveTab('available');
      await refetchMechanics();
      alert('Mechanic added successfully!');
    } catch (e) {
      alert(e?.message || String(e));
    } finally {
      setIsAddingMechanic(false);
    }
  };

  return (
    <div className="space-y-6 p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center space-x-4 mb-4">
        <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-lg">
          <ArrowLeft size={24} className="text-gray-700" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Select Mechanic</h1>
          <p className="text-gray-600">Complaint #{complaint.id?.slice(0, 8) || id?.slice(0, 8)}</p>
        </div>
      </div>

      {/* Complaint Summary */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Complaint Summary</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-500">Customer Name</p>
            <p className="font-medium text-gray-900">{complaint.customer_name || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Contact Number</p>
            <p className="font-medium text-gray-900">{complaint.contact_number || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Alternate Number</p>
            <p className="font-medium text-gray-900">{complaint.alternate_number || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Vehicle Number</p>
            <p className="font-medium text-gray-900">{complaint.vehicle_number || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Service Type</p>
            <p className="font-medium text-gray-900">{complaint.service_type || 'N/A'}</p>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">Pincode</p>
              {(!complaint.pincode || complaint.pincode === 'Not set') && (
                <button
                  onClick={() => {
                    setPincodeInput(complaint.pincode || '');
                    setShowPincodeModal(true);
                  }}
                  className="text-xs text-teal-600 hover:text-teal-700 flex items-center space-x-1"
                >
                  <Edit2 size={12} />
                  <span>Set</span>
                </button>
              )}
            </div>
            <p className="font-medium text-gray-900">{resolvedPincode || complaint.pincode || 'Not set'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Address</p>
            <p className="font-medium text-gray-900">{complaint.address || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Location Link</p>
            {complaint.location_link && complaint.location_link !== 'N/A' ? (
              <button
                onClick={handleLocationClick}
                className="font-medium text-teal-600 hover:text-teal-700 break-all flex items-center space-x-1 underline"
              >
                <span>{complaint.location_link}</span>
                <ExternalLink size={14} />
              </button>
            ) : (
              <p className="font-medium text-gray-900">N/A</p>
            )}
          </div>
          {complaint.problem && (
            <div className="md:col-span-2">
              <p className="text-sm text-gray-500">Problem Description</p>
              <p className="font-medium text-gray-900">{complaint.problem || 'N/A'}</p>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between gap-3 border-b border-gray-200">
          <div className="flex space-x-2">
          <button
            onClick={() => setActiveTab('available')}
            className={`px-4 py-2 font-medium ${
              activeTab === 'available'
                ? 'border-b-2 border-teal-500 text-teal-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Available Mechanics ({mechanics.length})
          </button>
          <button
            onClick={() => setActiveTab('search')}
            className={`px-4 py-2 font-medium ${
              activeTab === 'search'
                ? 'border-b-2 border-teal-500 text-teal-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Search Mechanics
          </button>
          </div>

          <button
            type="button"
            onClick={() => setShowAddMechanicModal(true)}
            className="px-3 py-2 rounded-lg bg-teal-600 text-white hover:bg-teal-700 text-sm font-medium flex items-center gap-2"
            title="Add mechanic (like mobile)"
          >
            <Plus size={16} />
            Add Mechanic
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search by name, phone, service tag, or pincode..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
          />
        </div>
      </div>

      {/* Mechanics List */}
      {activeTab === 'available' && (
        <div className="space-y-4">
          {isLoadingMechanics ? (
            <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-200 text-center">
              <Loader2 size={48} className="mx-auto text-teal-500 animate-spin mb-4" />
              <p className="text-gray-600">Loading mechanics...</p>
            </div>
          ) : filteredMechanics.length === 0 ? (
            <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-200 text-center">
              <Wrench size={48} className="mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500">No mechanics found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredMechanics.map((mechanic) => (
                <div
                  key={mechanic.id}
                  onClick={() => handleMechanicSelect(mechanic)}
                  className={`bg-white rounded-xl p-4 shadow-sm border-2 cursor-pointer transition ${
                    selectedMechanic?.id === mechanic.id
                      ? 'border-teal-500 bg-teal-50'
                      : 'border-gray-200 hover:border-teal-300'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                          <User size={24} className="text-teal-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">
                            {mechanic.c_name || mechanic.name || mechanic.mechanic_name || 'Unknown'}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {mechanic.contact_number || mechanic.number || 'N/A'}
                          </p>
                        </div>
                        {selectedMechanic?.id === mechanic.id && (
                          <CheckCircle size={24} className="text-teal-500" />
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm text-gray-600 ml-15">
                        {mechanic.pincode && (
                          <div className="flex items-center space-x-1">
                            <MapPin size={14} />
                            <span>Pincode: {mechanic.pincode}</span>
                          </div>
                        )}
                        {mechanic.service_tag && (
                          <div className="flex items-center space-x-1">
                            <Wrench size={14} />
                            <span>Service: {mechanic.service_tag}</span>
                          </div>
                        )}
                        {(mechanic.timing || mechanic.timings) && (
                          <div className="flex items-center space-x-1">
                            <Clock size={14} />
                            <span>Timing: {mechanic.timing || mechanic.timings}</span>
                          </div>
                        )}
                        {mechanic.service_areas && (
                          <div className="flex items-center space-x-1">
                            <MapPin size={14} />
                            <span>Areas: {formatServiceAreas(mechanic.service_areas)}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center space-x-2 mt-3 ml-15">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopyMechanicInfo(mechanic);
                          }}
                          className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center space-x-1 text-sm"
                          title="Copy Info"
                        >
                          <Copy size={14} />
                          <span>Copy</span>
                        </button>
                        {(mechanic.contact_number || mechanic.number) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCall(mechanic.contact_number || mechanic.number, mechanic);
                            }}
                            className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 flex items-center space-x-1 text-sm"
                            title="Call"
                          >
                            <Phone size={14} />
                            <span>Call</span>
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewLogs(mechanic);
                          }}
                          className="px-3 py-1.5 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 flex items-center space-x-1 text-sm"
                          title="View Logs"
                        >
                          <FileText size={14} />
                          <span>Logs</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Search Tab */}
      {activeTab === 'search' && (
        <div className="space-y-4">
          {isLoadingSearch ? (
            <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-200 text-center">
              <Loader2 size={48} className="mx-auto text-teal-500 animate-spin mb-4" />
              <p className="text-gray-600">Searching mechanics...</p>
            </div>
          ) : !debouncedSearchQuery.trim() ? (
            <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-200 text-center">
              <Search size={48} className="mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500 mb-2">Enter a search query to find mechanics</p>
              <p className="text-sm text-gray-400">Search by name, phone, service tag, pincode, or area</p>
            </div>
          ) : searchResults.length === 0 ? (
            <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-200 text-center">
              <Wrench size={48} className="mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500">No mechanics found for "{debouncedSearchQuery}"</p>
            </div>
          ) : (
            <div className="space-y-3">
              {searchResults.map((mechanic) => {
                // Handle both registered and unregistered mechanic formats
                const mechanicData = mechanic.mechanic_data || mechanic;
                const mechanicId = mechanic.id || mechanicData?.id || mechanicData?.place_id || `search-${Math.random()}`;
                const mechanicName = mechanicData?.name || mechanic.name || mechanic.mechanic_name || 'Unknown';
                const mechanicPhone = mechanicData?.phone || mechanicData?.contact_number || mechanic.contact_number || mechanic.number || 'N/A';
                const mechanicPincode = mechanicData?.pincode || mechanic.pincode || 'N/A';
                const mechanicServiceTag = mechanicData?.service_tag || mechanic.service_tag || 'N/A';
                const mechanicTiming = mechanicData?.timing || mechanicData?.timings || mechanic.timing || mechanic.timings || '';
                
                return (
                  <div
                    key={mechanicId}
                    onClick={() => handleMechanicSelect({
                      id: mechanicId,
                      name: mechanicName,
                      mechanic_name: mechanicName,
                      contact_number: mechanicPhone,
                      number: mechanicPhone,
                      pincode: mechanicPincode,
                      service_tag: mechanicServiceTag,
                      timing: mechanicTiming || undefined,
                      service_areas: mechanicData?.service_areas || mechanic.service_areas,
                      is_unregistered: !mechanic.id, // Mark as unregistered if no ID
                      search_result: mechanic // Keep original search result
                    })}
                    className={`bg-white rounded-xl p-4 shadow-sm border-2 cursor-pointer transition ${
                      selectedMechanic?.id === mechanicId
                        ? 'border-teal-500 bg-teal-50'
                        : 'border-gray-200 hover:border-teal-300'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                            <User size={24} className="text-blue-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">{mechanicName}</h3>
                            <p className="text-sm text-gray-600">{mechanicPhone}</p>
                            {!mechanic.id && (
                              <span className="text-xs text-blue-600 bg-blue-100 px-2 py-0.5 rounded mt-1 inline-block">
                                Unregistered
                              </span>
                            )}
                          </div>
                          {selectedMechanic?.id === mechanicId && (
                            <CheckCircle size={24} className="text-teal-500" />
                          )}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm text-gray-600 ml-15">
                          {mechanicPincode && mechanicPincode !== 'N/A' && (
                            <div className="flex items-center space-x-1">
                              <MapPin size={14} />
                              <span>Pincode: {mechanicPincode}</span>
                            </div>
                          )}
                          {mechanicServiceTag && mechanicServiceTag !== 'N/A' && (
                            <div className="flex items-center space-x-1">
                              <Wrench size={14} />
                              <span>Service: {mechanicServiceTag}</span>
                            </div>
                          )}
                          {!!mechanicTiming && (
                            <div className="flex items-center space-x-1">
                              <Clock size={14} />
                              <span>Timing: {mechanicTiming}</span>
                            </div>
                          )}
                          {mechanicData?.address && (
                            <div className="flex items-center space-x-1">
                              <MapPin size={14} />
                              <span>{mechanicData.address}</span>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center space-x-2 mt-3 ml-15">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const info = [
                                `Name: ${mechanicName}`,
                                `Phone: ${mechanicPhone}`,
                                `Pincode: ${mechanicPincode}`,
                                `Service Tag: ${mechanicServiceTag}`,
                                `Timing: ${mechanicTiming || 'N/A'}`,
                                `Address: ${mechanicData?.address || 'N/A'}`
                              ].join('\n');
                              navigator.clipboard.writeText(info);
                              alert('Mechanic info copied to clipboard!');
                            }}
                            className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center space-x-1 text-sm"
                            title="Copy Info"
                          >
                            <Copy size={14} />
                            <span>Copy</span>
                          </button>
                          {mechanicPhone && mechanicPhone !== 'N/A' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                window.location.href = `tel:${mechanicPhone}`;
                              }}
                              className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 flex items-center space-x-1 text-sm"
                              title="Call"
                            >
                              <Phone size={14} />
                              <span>Call</span>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Pincode Update Modal */}
      {showPincodeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Update Pincode</h2>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Pincode <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={pincodeInput}
                onChange={(e) => {
                  // Only allow digits and limit to 6 digits
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setPincodeInput(value);
                }}
                placeholder="Enter 6-digit pincode"
                maxLength={6}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Pincode must be exactly 6 digits
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowPincodeModal(false);
                  setPincodeInput('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdatePincode}
                disabled={isUpdatingPincode || pincodeInput.length !== 6}
                className="flex-1 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUpdatingPincode ? 'Updating...' : 'Update'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Selected Mechanic Summary & Proceed Button */}
      {selectedMechanic && (
        <div className="bg-teal-50 border-2 border-teal-500 rounded-xl p-6 sticky bottom-0">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Selected Mechanic:</p>
              <p className="font-semibold text-gray-900">
                {selectedMechanic.name || selectedMechanic.mechanic_name || 'Unknown'}
              </p>
              <p className="text-sm text-gray-600">
                {selectedMechanic.contact_number || selectedMechanic.number || 'N/A'}
              </p>
              {(selectedMechanic.timing || selectedMechanic.timings) && (
                <p className="text-sm text-gray-600">
                  Timing: {selectedMechanic.timing || selectedMechanic.timings}
                </p>
              )}
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => setSelectedMechanic(null)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Clear
              </button>
              <button
                onClick={handleProceed}
                className="px-6 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 flex items-center space-x-2"
              >
                <span>Proceed to Assignment</span>
                <ArrowLeft size={18} className="rotate-180" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Mechanic Modal */}
      {showAddMechanicModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-2xl rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 bg-teal-600 text-white flex items-center justify-between">
              <div className="font-semibold">Add Mechanic</div>
              <button
                type="button"
                className="p-2 rounded-lg hover:bg-white/15"
                onClick={() => setShowAddMechanicModal(false)}
                disabled={isAddingMechanic}
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Mechanic Name *</label>
                <input
                  value={newMechanic.mechanic_name}
                  onChange={(e) => setNewMechanic((p) => ({ ...p, mechanic_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  placeholder="e.g. Ravi Kumar"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                <input
                  value={newMechanic.number}
                  onChange={(e) => setNewMechanic((p) => ({ ...p, number: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  placeholder="10 digit number"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Timing</label>
                <input
                  value={newMechanic.timing}
                  onChange={(e) => setNewMechanic((p) => ({ ...p, timing: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  placeholder="e.g. 9am-9pm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Alt Number 1</label>
                <input
                  value={newMechanic.alternate_number1}
                  onChange={(e) => setNewMechanic((p) => ({ ...p, alternate_number1: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Alt Number 2</label>
                <input
                  value={newMechanic.alternate_number2}
                  onChange={(e) => setNewMechanic((p) => ({ ...p, alternate_number2: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Service Tag</label>
                <input
                  value={newMechanic.service_tag}
                  onChange={(e) => setNewMechanic((p) => ({ ...p, service_tag: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  placeholder="e.g. battery, tyre"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Service Tag 2</label>
                <input
                  value={newMechanic.service_tag2}
                  onChange={(e) => setNewMechanic((p) => ({ ...p, service_tag2: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                />
              </div>

              <div className="md:col-span-2">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <label className="block text-sm font-medium text-gray-700">Service Areas (max 20)</label>
                  <button
                    type="button"
                    onClick={() =>
                      setNewMechanic((p) => {
                        const arr = Array.isArray(p.service_areas) ? [...p.service_areas] : [];
                        if (arr.length >= 20) return p;
                        arr.push({ area: '', pincode: complaint?.pincode || '', state: '' });
                        return { ...p, service_areas: arr };
                      })
                    }
                    className="px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium flex items-center gap-2"
                    disabled={(newMechanic.service_areas?.length || 0) >= 20}
                    title="Add another service area"
                  >
                    <Plus size={14} />
                    Add Area
                  </button>
                </div>

                <div className="space-y-2">
                  {(Array.isArray(newMechanic.service_areas) ? newMechanic.service_areas : []).map((row, idx) => (
                    <div key={idx} className="grid grid-cols-1 md:grid-cols-12 gap-2">
                      <input
                        value={row?.area || ''}
                        onChange={(e) =>
                          setNewMechanic((p) => {
                            const arr = Array.isArray(p.service_areas) ? [...p.service_areas] : [];
                            arr[idx] = { ...(arr[idx] || {}), area: e.target.value };
                            return { ...p, service_areas: arr };
                          })
                        }
                        className="md:col-span-5 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                        placeholder="Area"
                      />
                      <input
                        value={row?.pincode || ''}
                        onChange={(e) =>
                          setNewMechanic((p) => {
                            const arr = Array.isArray(p.service_areas) ? [...p.service_areas] : [];
                            arr[idx] = { ...(arr[idx] || {}), pincode: e.target.value };
                            return { ...p, service_areas: arr };
                          })
                        }
                        className="md:col-span-3 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                        placeholder="Pincode"
                      />
                      <input
                        value={row?.state || ''}
                        onChange={(e) =>
                          setNewMechanic((p) => {
                            const arr = Array.isArray(p.service_areas) ? [...p.service_areas] : [];
                            arr[idx] = { ...(arr[idx] || {}), state: e.target.value };
                            return { ...p, service_areas: arr };
                          })
                        }
                        className="md:col-span-3 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                        placeholder="State"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setNewMechanic((p) => {
                            const arr = Array.isArray(p.service_areas) ? [...p.service_areas] : [];
                            if (arr.length <= 1) {
                              arr[0] = { area: '', pincode: complaint?.pincode || '', state: '' };
                              return { ...p, service_areas: arr };
                            }
                            arr.splice(idx, 1);
                            return { ...p, service_areas: arr };
                          })
                        }
                        className="md:col-span-1 w-full px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                        title="Remove"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>

                <p className="text-xs text-gray-500 mt-2">
                  Note: kisi area ko valid banane ke liye <b>Area + Pincode + State</b> teeno fill karo.
                </p>
              </div>
            </div>

            <div className="px-5 py-4 border-t border-gray-200 flex items-center justify-end gap-2">
              <button
                type="button"
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                onClick={() => setShowAddMechanicModal(false)}
                disabled={isAddingMechanic}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50"
                onClick={handleSaveNewMechanic}
                disabled={isAddingMechanic}
              >
                {isAddingMechanic ? 'Saving…' : 'Save Mechanic'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssignMechanic;

